import React, { useState, useEffect } from 'react';
import axios from 'axios';

const SmartPopup = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    business_type: '',
    city: '',
    trigger_type: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [hasShown, setHasShown] = useState(false);

  useEffect(() => {
    // Check if popup was already shown in this session
    const popupShown = sessionStorage.getItem('popupShown');
    if (popupShown) {
      setHasShown(true);
      return;
    }

    // Trigger 1: Timer - Show after 6 seconds
    const timerTrigger = setTimeout(() => {
      if (!hasShown) {
        triggerPopup('timer');
      }
    }, 6000);

    // Trigger 2: Scroll - Show at 35% scroll
    const scrollTrigger = () => {
      const scrolled = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
      if (scrolled > 35 && !hasShown) {
        triggerPopup('scroll');
      }
    };

    // Trigger 3: Exit Intent - Desktop
    const exitIntentTrigger = (e) => {
      if (e.clientY < 10 && !hasShown) {
        triggerPopup('exit_intent');
      }
    };

    // Trigger 4: Back Button - Mobile
    const backButtonTrigger = () => {
      if (!hasShown) {
        triggerPopup('exit_intent_mobile');
      }
    };

    window.addEventListener('scroll', scrollTrigger);
    document.addEventListener('mouseleave', exitIntentTrigger);
    
    // Back button detection for mobile
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', backButtonTrigger);

    return () => {
      clearTimeout(timerTrigger);
      window.removeEventListener('scroll', scrollTrigger);
      document.removeEventListener('mouseleave', exitIntentTrigger);
      window.removeEventListener('popstate', backButtonTrigger);
    };
  }, [hasShown]);

  const triggerPopup = (triggerType) => {
    setShowPopup(true);
    setHasShown(true);
    setFormData(prev => ({ ...prev, trigger_type: triggerType }));
    sessionStorage.setItem('popupShown', 'true');
    
    // Track popup open event
    if (window.dataLayer) {
      window.dataLayer.push({
        'event': 'popup_open',
        'popup_trigger': triggerType
      });
    }
  };

  const handleClose = () => {
    setShowPopup(false);
    
    // Track popup close event
    if (window.dataLayer) {
      window.dataLayer.push({
        'event': 'popup_close'
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/popup-lead`,
        formData
      );

      // Track popup form submission
      if (window.dataLayer) {
        window.dataLayer.push({
          'event': 'popup_form_submit',
          'business_type': formData.business_type,
          'trigger_type': formData.trigger_type
        });
      }

      setSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error) {
      console.error('Error submitting popup lead:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!showPopup) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl animate-scale-in" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-6 rounded-t-2xl relative">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-white hover:text-gray-200 transition-colors"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="text-center">
            <div className="text-4xl mb-2">🎁</div>
            <h2 className="text-2xl font-bold mb-2">Wait! Special Offer Inside</h2>
            <p className="text-green-100">Get exclusive business growth tips + Priority support</p>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          {success ? (
            <div className="text-center py-8">
              <div className="text-green-500 text-6xl mb-4">✓</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h3>
              <p className="text-gray-600">We'll contact you shortly with exclusive offers.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Your Name *</label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                    placeholder="Enter your name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Mobile Number *</label>
                  <input
                    type="tel"
                    required
                    pattern="[0-9]{10}"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                    placeholder="10-digit mobile number"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Business Type *</label>
                  <select
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                    value={formData.business_type}
                    onChange={(e) => setFormData({ ...formData, business_type: e.target.value })}
                  >
                    <option value="">Select your business type</option>
                    <option value="retail">Retail Store</option>
                    <option value="salon">Salon/Spa</option>
                    <option value="restaurant">Restaurant/Cafe</option>
                    <option value="clinic">Clinic/Hospital</option>
                    <option value="gym">Gym/Fitness</option>
                    <option value="hotel">Hotel/Resort</option>
                    <option value="automobile">Automobile Service</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">City (Optional)</label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                    placeholder="Your city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full mt-6 bg-gradient-to-r from-green-500 to-green-600 text-white py-4 rounded-lg font-bold text-lg hover:from-green-600 hover:to-green-700 transition-all transform hover:scale-105 disabled:opacity-50 shadow-lg"
                data-testid="popup-submit-button"
              >
                {loading ? 'Submitting...' : '🚀 Get Exclusive Access'}
              </button>

              <p className="text-xs text-gray-500 text-center mt-4">
                We respect your privacy. No spam, only valuable business insights.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default SmartPopup;
